import { useState, type FormEvent } from 'react'
import { defaultLandingPolicy, type CheckScope, type LandingPolicy, type Project } from '../api/types'
import { activeRunners, agentName } from '../code/format'
import { useMounted } from '../components/layout'
import { needsServer } from '../components/needsServer'
import { toast } from '../components/toasts'
import { useUnsavedChanges } from '../components/unsaved'
import { confirmsActions } from '../people/actions'
import { useRunners } from '../sessions/context'
import { useBoardState, useStore } from '../store/context'
import { useSettingsProject } from './selection'
import { settingsWords } from './words'

const words = settingsWords.flow
const lines = (text: string) => text.split('\n').map(line => line.trim()).filter(Boolean)
/** The choice of a person's review: no agent's name has a colon (AgentName). */
const PERSON = ':person'

export function FlowSettings() {
  const project = useSettingsProject()
  return project ? <FlowForm key={project.id} project={project} /> : null
}

function FlowForm({ project }: { project: Project }) {
  const store = useStore()
  const { offline, me } = useBoardState()
  const mounted = useMounted()
  const { runners } = useRunners()
  const saved = project.landing_policy ?? defaultLandingPolicy()
  const [edit, setEdit] = useState<LandingPolicy | null>(null)
  const policy = edit ?? saved
  // Keep blank lines while typing, then trim them for the server.
  const [paths, setPaths] = useState<{ person: string; checks: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const dirty = edit !== null || paths !== null
  useUnsavedChanges(dirty)
  const change = (patch: Partial<LandingPolicy>) => { setEdit({ ...policy, ...patch }); setProblem(null) }
  const offers = activeRunners(runners).flatMap(r => r.agents).filter(a => a.signed_in === true)
  const person = policy.review === 'person'
  const reviewer = person ? null : policy.reviewer
  const agents = [...new Set([...offers.map(a => a.agent), ...(reviewer ? [reviewer.agent] : [])])]
  const models = [...new Set([...offers.filter(a => a.agent === reviewer?.agent).flatMap(a => a.models), ...(reviewer?.model ? [reviewer.model] : [])])]
  const needsModel = !!reviewer && models.length > 0 && !reviewer.model
  const checksUrl = project.repository ? `https://github.com/${project.repository}/blob/${encodeURIComponent(project.branch)}/.poulpe/check.toml` : null

  async function save(event: FormEvent) {
    event.preventDefault()
    if (store.getState().offline || busy || !dirty || needsModel) return
    setBusy(true); setProblem(null)
    const outcome = await store.setProjectPolicy(project.id, { ...policy,
      person_paths: lines(paths?.person ?? policy.person_paths.join('\n')),
      check_paths: lines(paths?.checks ?? policy.check_paths.join('\n')),
    })
    if (!mounted.current) return
    setBusy(false)
    if (outcome.ok) { setEdit(null); setPaths(null); toast(settingsWords.saved) }
    else setProblem(outcome.message)
  }

  return <section className="settings-section">
    <form className="setup-form" onSubmit={event => void save(event)}>
      <h2 className="settings-title">{words.checks}</h2>
      <p className="muted">{words.checksAbout}</p>
      {checksUrl && <p><a href={checksUrl} target="_blank" rel="noreferrer">{words.openChecks}</a></p>}
      <fieldset className="setup-form settings-flow-fields" disabled={busy}>
        <label className="field"><span>{words.checkPaths}</span><textarea rows={3} value={paths?.checks ?? policy.check_paths.join('\n')} onChange={event => {
          setEdit(policy); setPaths({ person: paths?.person ?? policy.person_paths.join('\n'), checks: event.target.value })
        }} /></label>
        <label className="field"><span>{words.landingCheck}</span><select value={policy.check_scope ?? 'full'} onChange={event => change({ check_scope: event.target.value as CheckScope })}>
          <option value="full">{words.everyPart}</option>
          <option value="affected">{words.affectedParts}</option>
        </select></label>
        <p className="muted">{words.landingCheckAbout}</p>
        <label className="field"><span>{words.reviews}</span><select value={person ? PERSON : reviewer?.agent ?? ''} onChange={event => {
          const chosen = event.target.value
          // A person's review needs no agent: the policy keeps none.
          change(chosen === PERSON ? { review: 'person', reviewer: null } : { review: undefined, reviewer: chosen ? { agent: chosen, model: null } : null })
        }}>
          <option value="">{words.automatic}</option>
          {agents.map(agent => <option key={agent} value={agent}>{agentName(agent)}</option>)}
          <option value={PERSON}>{words.person}</option>
        </select></label>
        {reviewer && <label className="field"><span>{words.model}</span><select value={reviewer.model ?? ''} disabled={models.length === 0} onChange={event => change({ reviewer: { ...reviewer, model: event.target.value || null } })}>
          {!reviewer.model && <option value="" disabled>{models.length ? words.chooseModel : words.noModels}</option>}
          {models.map(model => <option key={model} value={model}>{model}</option>)}
        </select></label>}
        <p className="muted">{person ? words.personAbout : words.reviewAbout}</p>
        <div className="field"><span>{words.merges}</span><span>{words.fastForward}</span></div>
        <h2 className="settings-title">{words.approval}</h2>
        <p className="muted">{words.approvalAbout(confirmsActions(me))}</p>
        <label className="field"><span>{words.personPaths}</span><textarea rows={3} value={paths?.person ?? policy.person_paths.join('\n')} onChange={event => {
          setEdit(policy); setPaths({ checks: paths?.checks ?? policy.check_paths.join('\n'), person: event.target.value })
        }} /></label>
        <p className="muted">{words.patterns}</p>
        <label><input type="checkbox" checked={policy.checks_need_person} onChange={event => change({ checks_need_person: event.target.checked })} /> {words.checksNeedPerson}</label>
        {problem && <p role="alert" className="text-danger">{problem}</p>}
        <div className="form-actions"><button className="button button-primary" disabled={offline || busy || !dirty || needsModel} {...needsServer(offline)}>{busy ? words.saving : words.save}</button></div>
      </fieldset>
    </form>
  </section>
}
